import { API_ORIGIN, MARKETING_ORIGIN } from "./constants"

// Values of the `?tab=` param on the org settings route. Rendered as tab
// labels by `components/settings/Settings.tsx`.
export enum SettingsTab {
  Profile = "Profile",
  BuyBox = "Buy box",
  Users = "Users",
  Portfolios = "Portfolios",
}

type NEXT_URL =
  | ["/404"]
  | ["/admin"]
  | ["/admin/api-keys"]
  | ["/admin/projects"]
  | ["/admin/documents"]
  | ["/admin/documents/[documentPid]", { documentPid: string }]
  | ["/admin/lease-analysis"]
  | ["/admin/finmodel"]
  | ["/admin/finmodel/versions"]
  | ["/admin/finmodel/failures"]
  | ["/admin/finmodel/failures/facet"]
  | ["/admin/finmodel/failures/[id]", { id: number }]
  | ["/admin/tariffs/[tariffId]", { tariffId: number }]
  | [
      "/admin/tariffs/[tariffId]/revisions/[revisionId]",
      { tariffId: number; revisionId: number },
    ]
  | [
      "/admin/tariffs/[tariffId]/revisions/draft/[draftPid]",
      { tariffId: number; draftPid: string },
    ]
  | ["/admin/orgs"]
  | ["/admin/orgs/[orgPid]", { orgPid: string }]
  | ["/admin/developer-contracts"]
  | ["/admin/developer-contracts/[orgPid]", { orgPid: string }]
  | ["/admin/developer-contracts/[orgPid]?tab=[tab]", { orgPid: string; tab: string }]
  | ["/admin/orgs/[orgPid]/products", { orgPid: string }]
  | ["/admin/orgs/[orgPid]/products/[id]", { orgPid: string; id: number }]
  | ["/admin/portfolios"]
  | ["/admin/portfolios/[pid]", { pid: string }]
  | ["/admin/portfolios/[pid]/energy", { pid: string }]
  | ["/admin/portfolios/[pid]/energy/logins", { pid: string }]
  | [
      "/admin/portfolios/[pid]/energy/logins/[loginId]",
      { pid: string; loginId: number },
    ]
  | ["/admin/portfolios/[pid]/tasks", { pid: string }]
  | ["/admin/orgs/[pid]/physical-check", { pid: string }]
  | [
      "/admin/orgs/[pid]/physical-check?pids=[sitePids]",
      { pid: string; sitePids: string },
    ]
  | ["/admin/portfolios/[pid]/bulk", { pid: string }]
  | ["/admin/multiportfolio"]
  | ["/admin/queue"]
  | ["/admin/job-activity"]
  | ["/admin/slack-channels"]
  | ["/admin/bulk-update"]
  | ["/admin/document-reprocess"]
  | ["/admin/solar-captcha"]
  | ["/admin/solar-detection"]
  | ["/admin/batteries"]
  | ["/admin/workstreams"]
  | ["/admin/ownership-review"]
  | ["/admin/ftm-programs"]
  | ["/admin/ftm-pricing"]
  | ["/admin/utilities"]
  | ["/admin/utilities/[slug]", { slug: string }]
  | ["/admin/utilities/[slug]/tariffs", { slug: string }]
  | ["/admin/urdb"]
  | ["/admin/urdb/utilities/[eiaId]", { eiaId: number }]
  | ["/admin/urdb/rates/[urdbId]", { urdbId: string }]
  | ["/admin/genability"]
  // `?tab=tariffs` / `?tab=revisions` open the portal on another table.
  | ["/admin/genability?tab=tariffs"]
  | ["/admin/genability?tab=revisions"]
  | ["/admin/genability?tab=unlinked"]
  | ["/admin/genability/utilities/[lseId]", { lseId: number }]
  | ["/admin/genability/tariffs/[masterTariffId]", { masterTariffId: number }]
  | ["/admin/genability/revisions/[revisionId]", { revisionId: number }]
  | ["/admin/sites"]
  // The all-sites admin page opens on its table tab by default; `?tab=map`
  // deep-links to the map tab (see `components/admin/Search.tsx`).
  | ["/admin/sites?tab=map"]
  // `?tab=utility` deep-links to the utility-assignment tab, where guessed
  // utility assignments are reviewed and fixed in bulk.
  | ["/admin/sites?tab=utility"]
  | ["/admin/sites/[sitePid]", { sitePid: string }]
  | ["/admin/sites/[sitePid]/changelog", { sitePid: string }]
  | ["/admin/sites/[sitePid]/finmodels", { sitePid: string }]
  | ["/admin/sites/[sitePid]/finmodels/[id]", { sitePid: string; id: number }]
  | [
      "/admin/sites/[sitePid]/finmodels/[a]/compare?other=[b]",
      { sitePid: string; a: number; b: number },
    ]
  | ["/admin/sites/[sitePid]/results", { sitePid: string }]
  | ["/admin/sites/[sitePid]/results/[id]", { sitePid: string; id: number }]
  | ["/admin/sites/[sitePid]/projects", { sitePid: string }]
  | [
      "/admin/sites/[sitePid]/projects?project=[projectPid]",
      { sitePid: string; projectPid: string },
    ]
  | ["/admin/sites/[sitePid]/etb", { sitePid: string }]
  | ["/admin/sites/[sitePid]/etb/[etbId]", { sitePid: string; etbId: string | number }]
  | ["/admin/sites/[sitePid]/bill-entry", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy/billing-meters", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy/espm", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy/meters", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy/multi-meter", { sitePid: string }]
  | ["/admin/sites/[sitePid]/energy/multi-meter/[id]", { sitePid: string; id: number }]
  | [
      "/admin/sites/[sitePid]/energy/multi-meter/[id]/name-that-tariff",
      { sitePid: string; id: number },
    ]
  | [
      "/admin/sites/[sitePid]/energy/multi-meter/set/[id]",
      { sitePid: string; id: number },
    ]
  | ["/admin/sites/[sitePid]/helioscopes", { sitePid: string }]
  | ["/admin/sites/[sitePid]/lux/[id]", { sitePid: string; id: number }]
  | ["/admin/sites/[sitePid]/building-data", { sitePid: string }]
  | ["/admin/sites/[sitePid]/policy", { sitePid: string }]
  | ["/admin/sites/[sitePid]/scenarios", { sitePid: string }]
  | ["/admin/sites/[sitePid]/scenarios/[pid]", { sitePid: string; pid: string }]
  | ["/admin/sites/[sitePid]/status", { sitePid: string }]
  | ["/admin/sites/[sitePid]/utils/pdf-test", { sitePid: string }]
  | ["/admin/sites/[sitePid]/utils", { sitePid: string }]
  | ["/admin/buildings"]
  | ["/admin/buildings/add"]
  | ["/admin/buildings/add/single"]
  | ["/admin/buildings/add/upload"]
  | ["/admin/buildings/add/multi/[csvImportPid]", { csvImportPid: string }]
  | ["/admin/buildings/[buildingPid]", { buildingPid: string }]
  | ["/admin/companies"]
  | ["/admin/companies/duplicates"]
  | ["/admin/companies/[companyPid]", { companyPid: string }]
  | ["/admin/interconnection-segments"]
  | ["/admin/interconnection-segments/[pk]", { pk: string }]
  // Deep-links straight into the org-scoped prospecting view -- see
  // `ProspectingPage`'s `orgPid` query param.
  | [
      "/admin/interconnection-segments?tab=prospecting&orgPid=[orgPid]",
      { orgPid: string },
    ]
  | [
      "/admin/substations/[utilityId]/[substation]",
      { utilityId: number; substation: string },
    ]
  | ["/admin/policies"]
  | ["/admin/policies/[pid]", { pid: string }]
  | ["/admin/users"]
  | ["/admin/users/[userId]", { userId: number }]
  | ["/admin/users/new"]
  | ["/admin/flower"]
  | ["/admin/impact"]
  | ["/admin/xlsx"]
  | ["/admin/announcements"]
  | ["/[orgSlug]", { orgSlug: string }]
  | ["/[orgSlug]/welcome", { orgSlug: string }]
  | ["/[orgSlug]/energize", { orgSlug: string }]
  | ["/[orgSlug]/portfolio", { orgSlug: string }]
  | ["/[orgSlug]/properties", { orgSlug: string }]
  | ["/[orgSlug]/properties/add", { orgSlug: string }]
  | ["/[orgSlug]/properties/[sitePid]", { orgSlug: string; sitePid: string }]
  | ["/[orgSlug]/properties/[sitePid]/documents", { orgSlug: string; sitePid: string }]
  | ["/[orgSlug]/properties/[sitePid]/ev", { orgSlug: string; sitePid: string }]
  | ["/[orgSlug]/properties/[sitePid]/solar", { orgSlug: string; sitePid: string }]
  | ["/[orgSlug]/properties/[sitePid]/solar/bess", { orgSlug: string; sitePid: string }]
  | [
      "/[orgSlug]/properties/[sitePid]/solar/[resultPid]",
      { orgSlug: string; sitePid: string; resultPid: string },
    ]
  | ["/[orgSlug]/properties/[sitePid]/general", { orgSlug: string; sitePid: string }]
  | [
      "/[orgSlug]/properties/[sitePid]/electricity",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/electricity/tariffs/custom/[tariffId]",
      { orgSlug: string; sitePid: string; tariffId: number },
    ]
  | ["/[orgSlug]/properties/[sitePid]/configure", { orgSlug: string; sitePid: string }]
  | ["/[orgSlug]/properties/[sitePid]/transact", { orgSlug: string; sitePid: string }]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/preparing-bid-launch",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/collecting-bids",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/reviewing-bids",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/negotiating-contracts",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/developing-project",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/under-construction",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/energize",
      { orgSlug: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/preparing-bid-launch",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/collecting-bids",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/reviewing-bids",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/negotiating-contracts",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/developing-project",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/under-construction",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/properties/[sitePid]/marketplace/[projectPid]/energize",
      { orgSlug: string; sitePid: string; projectPid: string },
    ]
  | ["/[orgSlug]/marketplace", { orgSlug: string }]
  | ["/[orgSlug]/marketplace/bids", { orgSlug: string }]
  | ["/[orgSlug]/marketplace/properties", { orgSlug: string }]
  | ["/[orgSlug]/marketplace/packages", { orgSlug: string }]
  | [
      "/[orgSlug]/marketplace/packages/[bundlePid]",
      { orgSlug: string; bundlePid: string },
    ]
  | [
      "/[orgSlug]/marketplace/packages/[bundlePid]/emails",
      { orgSlug: string; bundlePid: string },
    ]
  | [
      "/[orgSlug]/marketplace/packages/[bundlePid]/project/[projectPid]",
      { orgSlug: string; bundlePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/marketplace/packages/[bundlePid]/project/[projectPid]/bid/[bidPid]",
      {
        orgSlug: string
        bundlePid: string
        projectPid: string
        bidPid: string
      },
    ]
  | ["/[orgSlug]/bundles", { orgSlug: string }]
  | ["/[orgSlug]/bundles/parents", { orgSlug: string }]
  | ["/[orgSlug]/bundles/parents/create", { orgSlug: string }]
  | ["/[orgSlug]/bundles/parents/[pid]", { orgSlug: string; pid: string }]
  | ["/[orgSlug]/bundles/parents/[pid]/edit", { orgSlug: string; pid: string }]
  | ["/[orgSlug]/bundles/parents/[pid]/emails", { orgSlug: string; pid: string }]
  | ["/[orgSlug]/bundles/parents/[pid]/award", { orgSlug: string; pid: string }]
  | ["/[orgSlug]/bundles/parents/[pid]/award-notices", { orgSlug: string; pid: string }]
  | ["/[orgSlug]/invest", { orgSlug: string }]
  | ["/[orgSlug]/invest/origination", { orgSlug: string }]
  | [
      "/[orgSlug]/invest/origination/[bundlePid]",
      { orgSlug: string; bundlePid: string },
    ]
  | [
      "/[orgSlug]/invest/origination/[bundlePid]/reports",
      { orgSlug: string; bundlePid: string },
    ]
  | [
      "/[orgSlug]/invest/origination/[bundlePid]/project/[projectPid]/create-bid",
      { orgSlug: string; bundlePid: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/invest/origination/[bundlePid]/project/[projectPid]/bid/[bidPid]",
      {
        orgSlug: string
        bundlePid: string
        projectPid: string
        bidPid: string
      },
    ]
  // Legacy site-keyed bid routes, kept so old links still resolve; both
  // redirect to the package, since a site can hold several projects.
  | [
      "/[orgSlug]/invest/origination/[bundlePid]/property/[sitePid]/create-bid",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/origination/[bundlePid]/property/[sitePid]/bid/[bidPid]",
      {
        orgSlug: string
        bundlePid: string
        sitePid: string
        bidPid: string
      },
    ]
  | ["/[orgSlug]/invest/meeting-notes", { orgSlug: string }]
  | ["/[orgSlug]/invest/meeting-notes/[pid]", { orgSlug: string; pid: string }]
  | [
      "/[orgSlug]/invest/meeting-notes/[pid]/extractions",
      { orgSlug: string; pid: string },
    ]
  | [
      "/[orgSlug]/invest/meeting-notes/[pid]/extractions/[extractionPid]",
      { orgSlug: string; pid: string; extractionPid: string },
    ]
  | ["/[orgSlug]/invest/development", { orgSlug: string }]
  | ["/[orgSlug]/invest/[bundlePid]", { orgSlug: string; bundlePid: string }]
  | [
      "/[orgSlug]/invest/development/[bundlePid]/property/[sitePid]",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/development/[bundlePid]/property/[sitePid]/documents",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/development/[bundlePid]/property/[sitePid]/bids",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/development/project/[projectPid]",
      { orgSlug: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/invest/development/project/[projectPid]/documents",
      { orgSlug: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/invest/development/project/[projectPid]/bids",
      { orgSlug: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/invest/development/project/[projectPid]/milestones",
      { orgSlug: string; projectPid: string },
    ]
  | [
      "/[orgSlug]/invest/[bundlePid]/property/[sitePid]",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/[bundlePid]/property/[sitePid]/documents",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/[bundlePid]/property/[sitePid]/bids",
      { orgSlug: string; bundlePid: string; sitePid: string },
    ]
  | [
      "/[orgSlug]/invest/settings?tab=[tab]",
      { tab: SettingsTab | undefined; orgSlug: string },
    ]
  | ["/[orgSlug]/invest/settings", { orgSlug: string }]
  | ["/[orgSlug]/projects", { orgSlug: string }]
  | ["/[orgSlug]/settings?tab=[tab]", { tab: SettingsTab | undefined; orgSlug: string }]
  | ["/[orgSlug]/settings", { orgSlug: string }]
  | ["/[orgSlug]/uploads", { orgSlug: string }]
  | ["/[orgSlug]/uploads/[csvImportPid]", { orgSlug: string; csvImportPid: string }]
  | [
      "/[orgSlug]/uploads/[csvImportPid]/rows/[rowId]",
      { orgSlug: string; csvImportPid: string; rowId: number },
    ]
  | ["/[orgSlug]/reonomy", { orgSlug: string }]
  | ["/[orgSlug]/org-emails", { orgSlug: string }]
  | ["/connect/"]
  | ["/connect/[pid]", { pid: string }]
  | ["/login"]

type FLASK_URL =
  | ["/admin/attachments/[id]", { id: string | number }]
  | ["/dl/sample_import.csv"]
  | ["/estimates"]
  | ["/logout"]
  | ["/buildings/[sitePid]/reopt", { sitePid: string }]
  | ["/buildings/[sitePid]/reopt/[reoptPid]", { sitePid: string; reoptPid: string }]
  | [
      "/portfolio/[portfolioPid]/sites/[sitePid]/cash_flow_model",
      { portfolioPid: string; sitePid: string },
    ]
  | [
      "/offtaker/[portfolioPid]/sites/[sitePid]/cash_flow_model",
      { portfolioPid: string; sitePid: string },
    ]
  | [
      "/portfolio/[portfolioPid]/sites/[sitePid]/clean-energy-report.pdf",
      { portfolioPid: string; sitePid: string },
    ]
  | ["/sites/[sitePid]/intervals", { sitePid: string }]
  | [
      "/sites/[sitePid]/intervals?finmodel=[finmodelId]",
      { sitePid: string; finmodelId: number },
    ]
  | ["/sites/[sitePid]/intervals/[finmodelId]", { sitePid: string; finmodelId: number }]
  | ["/auth/google/login"]

type MARKETING_URL =
  | ["/"]
  | ["/legal/privacy-policy"]
  | ["/legal/terms-of-service"]
  | ["/legal/terms-of-use"]
  | ["/help/product"]
  | ["/resources/how-does-lumen-energy-evaluate-bids/"]
  | ["/resources/how-going-solar-raises-commercial-property-value"]
  | ["/resources/how-does-lumen-determine-the-optimal-amount-of-solar-for-a-building"]
  | ["/resources/net-zero-and-its-impact-on-institutional-investors"]
  | ["/resources/providing-energy-data"]
  | [
      "/resources/reduce-your-carbon-emissions-renewable-energy-vs-energy-efficiency-upgrades",
    ]
  | ["/resources/solar-financing-terms-explained"]
  | ["/resources/understanding-solar-power-purchase-agreements"]
  | [
      "/resources/unlock-the-benefits-of-solar-investment-tax-credits-for-your-commercial-real-estate-properties",
    ]
  | ["/resources/what-financing-options-are-available"]
  | ["/resources/your-guide-to-the-lumen-journey"]
  | ["/resources/your-step-by-step-guide-to-going-solar-with-ppa-financing"]
  | ["/resources/community-solar-101"]

/**
 * react-router path patterns used to *match* the current location
 * (`useMatch` / `matchPath`), as opposed to the concrete URLs `url()` builds.
 * Keep every matched pattern here so no raw path strings hide at call sites
 * and a router migration finds them all in one place.
 */
export const ROUTE_PATTERNS = {
  propertyHub: "/:orgSlug/properties",
  addProperty: "/:orgSlug/properties/add",
  propertyDetail: "/:orgSlug/properties/:sitePid",
  tariffEditor: "/:orgSlug/properties/:sitePid/electricity/tariffs/custom/:tariffId",
  invest: "/:orgSlug/invest",
  physicalCheck: "/admin/orgs/:orgPid/physical-check",
} as const

export function url([pathname, query]: NEXT_URL): string {
  // It would be nice to return {pathname, query} URL objects as supported by `<Link>`,
  // but that breaks client-side transitions for paths like
  // /portfolio/.../{evaluate,bids,documents} which are defined with [[...params]].
  return query
    ? // @ts-ignore
      pathname.replaceAll(/\[([^\]]+)\]/g, (_, key) => encodeURIComponent(query[key]))
    : pathname
}

/**
 * Link to a site's projects admin page, optionally deep-linked to a specific
 * project tab. When `projectPid` is provided the URL carries `?project=<pid>`
 * so the page opens on that project; otherwise it links to the default tab.
 */
export function adminSiteProjectsUrl({
  sitePid,
  projectPid,
}: {
  sitePid: string
  projectPid?: string | null
}): string {
  return projectPid
    ? url([
        "/admin/sites/[sitePid]/projects?project=[projectPid]",
        { sitePid, projectPid },
      ])
    : url(["/admin/sites/[sitePid]/projects", { sitePid }])
}

export function flaskUrl([pathname, query]: FLASK_URL): string {
  const url = pathname.replaceAll(
    /\[([^\]]+)\]/g,
    // @ts-ignore
    (_, key: keyof typeof query) => encodeURIComponent(query[key]),
  )
  return API_ORIGIN + url
}

export function marketingUrl([pathname]: MARKETING_URL): string {
  return MARKETING_ORIGIN + pathname
}

export function hubspotRecordUrl(hubspotRecordId: string): string {
  return `https://app.hubspot.com/contacts/20832032/record/0-3/${hubspotRecordId}`
}

export function hubspotCompanyUrl(hubspotId: string): string {
  return `https://app.hubspot.com/contacts/20832032/record/0-2/${hubspotId}`
}

export function reonomyCompanyUrl(reonomyOwnerId: string): string {
  return `https://app.reonomy.com/!/company/${reonomyOwnerId}/overview`
}
